import React, { useRef } from "react";
import { connect, useDispatch } from "react-redux";
import { goBack } from "connected-react-router";
import PropTypes from "prop-types";
import MainContainer from "pages/_components/MainContainer";
import { ResponsiveHeader } from "pages/_components/header";
import Button from "pages/_components/Button";
import { Field, Form, Formik } from "formik";
import CustomText from "pages/_components/CustomText/CustomText";
import Credential from "pages/_components/fields/credentials/Credential";
import GridTable from "pages/_components/GridTable/GridTable";
import I18n from "pages/_components/I18n";
import Notification from "pages/_components/Notification";
import { actions as recoveryPasswordActions } from "reducers/recoveryPassword";
import * as i18nUtils from "util/i18n";
import * as configUtils from "util/config";
import * as Yup from "yup";
import ToDoList from "../onboarding/_components/ToDoList";

const FORM_ID = "resetExpiredPassword.step1";

const Mustache = require("mustache");

const ResetExpiredPassword = ({ isMobile }) => {
    const dispatch = useDispatch();
    const formRef = useRef(null);

    const minLength = configUtils.getInteger("core.password.minLength");
    const maxLength = configUtils.getInteger("core.password.maxLength");
    const minLengthValidationMsg = Mustache.render(i18nUtils.get("onboarding.password.validation.minLength"), {
        minLength,
        maxLength,
    });
    const maxLengthValidationMsg = Mustache.render(i18nUtils.get("onboarding.password.validation.maxLength"), {
        maxLength,
    });

    const onHeaderBack = () => {
        dispatch(goBack());
    };

    // listo
    const formikProps = {
        validateOnChange: true,
        validateOnBlur: false,
        ref: formRef,
        initialValues: {
            password: "",
            newPassword: "",
            repeatedPassword: "",
        },
        validationSchema: Yup.lazy((values) =>
            Yup.object().shape({
                password: Yup.string().required(i18nUtils.get("enrollment.step3.password.error.empty")),
                newPassword: Yup.string()
                    .required(i18nUtils.get("enrollment.step3.password.error.empty"))
                    .min(minLength, minLengthValidationMsg)
                    .max(maxLength, maxLengthValidationMsg)
                    .matches(
                        new RegExp(configUtils.get("core.userconfiguration.password.pattern"), "g"),
                        i18nUtils.get("enrollment.step3.password.error.unfulfilledRules"),
                    )
                    .oneOf(
                        [values.repeatedPassword],
                        i18nUtils.get("enrollment.step3.password.error.passwordConfirmationMustMatch"),
                    ),
                repeatedPassword: Yup.string()
                    .matches(
                        new RegExp(configUtils.get("core.userconfiguration.password.pattern"), "g"),
                        i18nUtils.get("enrollment.step3.password.error.unfulfilledRules"),
                    )
                    .oneOf(
                        [values.newPassword],
                        i18nUtils.get("enrollment.step3.password.error.passwordConfirmationMustMatch"),
                    ),
            }),
        ),
        onSubmit: ({ password, newPassword, repeatedPassword }, formikBag) => {
            if (password && newPassword && repeatedPassword) {
                // dispatch(onboardingActions.setPassword(password, repeatedPassword));
                // dispatch(push("/onboarding/stepSelectSecuritySeal"));
                dispatch(
                    recoveryPasswordActions.resetExpiredPasswordRequest(
                        password,
                        newPassword,
                        repeatedPassword,
                        formikBag,
                    ),
                );
            }
        },
    };

    // listo
    const validations = [
        {
            description: minLengthValidationMsg,
            status: (password) => {
                if (!password) {
                    return "INITIAL";
                }
                return password?.length >= minLength && password?.length <= maxLength ? "DONE" : "WRONG";
            },
        },
        {
            description: i18nUtils.get("onboarding.password.validation2", "Mínimo 1 minúscula."),
            status: (password) => {
                if (!password) {
                    return "INITIAL";
                }
                return /[a-z]/.test(password) ? "DONE" : "WRONG";
            },
        },
        {
            description: i18nUtils.get("onboarding.password.validation1", "Mínimo 1 mayúscula."),
            status: (password) => {
                if (!password) {
                    return "INITIAL";
                }
                return /[A-Z]/.test(password) ? "DONE" : "WRONG";
            },
        },
        {
            description: i18nUtils.get("onboarding.password.validation3", "Mínimo 1 número."),
            status: (password) => {
                if (!password) {
                    return "INITIAL";
                }
                return /\d/.test(password) ? "DONE" : "WRONG";
            },
        },
        {
            description: i18nUtils.get(
                "onboarding.password.validation4",
                "Mínimo 1 de los siguientes símbolos @$#!%&()_+{}?*",
            ),
            status: (password) => {
                if (!password) {
                    return "INITIAL";
                }
                const symbols = /[@$#¡!%&()_{}?¿*]/;
                return symbols.test(password) ? "DONE" : "WRONG";
            },
        },
    ];

    const notContainValidations = [
        {
            description: i18nUtils.get("onboarding.password.validation5", "Más de 2 caracteres repetidos. Ej: AA"),
            status: (password) => {
                if (!password) {
                    return "INITIAL";
                }
                return /([a-zA-Z])\1/.test(password) ? "WRONG" : "DONE";
            },
        },
        {
            description: i18nUtils.get(
                "onboarding.password.validation6",
                "Más de 3 caracteres consecutivos. Ej: 1234 o abcd",
            ),
            status: (password) => {
                if (!password) {
                    return "INITIAL";
                }
                return /(0123|1234|2345|3456|4567|5678|6789|abcd|bcde|cdef|defg|efgh|fghi|ghij|hijk|ijkl|jklm|klmn|lmnñ|mnño|nñop|ñopq|opqr|pqrs|qrst|rstu|stuv|tuvw|uvwx|vwxy|wxyz)+/.test(
                    password,
                )
                    ? "WRONG"
                    : "DONE";
            },
        },
    ];

    // listo
    const passwordStrength = (password) => {
        const numInvalidValidations = validations.reduce(
            (invalids, val) => (val.status(password) !== "DONE" ? invalids + 1 : invalids),
            0,
        );
        const numInvalidNotContainValidations = notContainValidations.reduce(
            (invalids, val) => (val.status(password) !== "DONE" ? invalids + 1 : invalids),
            0,
        );

        const totalNumInvalidValidations = numInvalidValidations + numInvalidNotContainValidations;

        if (totalNumInvalidValidations === 0) {
            return 67;
        }

        if (totalNumInvalidValidations <= 1) {
            return 34;
        }

        if (password) {
            return 1;
        }

        return -1;
    };

    const getItems = (password) =>
        validations.map((val) => ({ status: val.status(password), description: val.description }));

    const getNotContainItems = (password) =>
        notContainValidations.map((val) => ({
            status: val.status(password),
            description: val.description,
        }));

    const firstValidation = {
        description: minLengthValidationMsg,
        status: (password) => {
            if (!password) {
                return "INITIAL";
            }
            return password?.length >= minLength && password?.length <= maxLength ? "DONE" : "WRONG";
        },
    };

    // listo
    const submitIsDisabled = ({ password, newPassword, repeatedPassword }) => {
        const firstValidationWrong = firstValidation.status(newPassword) !== "DONE";

        const numInvalidValidations = validations.reduce(
            (invalids, val) => (val.status(newPassword) !== "DONE" ? invalids + 1 : invalids),
            0,
        );
        const numInvalidNotContainValidations = notContainValidations.reduce(
            (invalids, val) => (val.status(newPassword) !== "DONE" ? invalids + 1 : invalids),
            0,
        );

        const totalNumInvalidValidations = numInvalidValidations + numInvalidNotContainValidations;
        const repeatedPasswordIsDifferent = repeatedPassword !== newPassword;

        return firstValidationWrong || totalNumInvalidValidations > 0 || repeatedPasswordIsDifferent;
    };

    return (
        <>
            {" "}
            {isMobile && (
                <ResponsiveHeader id="onboarding.noUser.title" onBack={isMobile && onHeaderBack} showCenterContent />
            )}
            <Notification scopeToShow="resetPassword" />
            <MainContainer>
                {/* {!isMobile && <Head title="onboarding.noUser.title" onBack={isMobile && onHeaderBack} />} */}

                <h1 className="text-center" style={{ textAlign: "left" }}>
                    <CustomText text={i18nUtils.get("", "Tu contraseña expiró. Actualizala para seguir operando.")} />
                </h1>

                <Formik {...formikProps}>
                    {(allFormik) => {
                        const validationFields = [
                            <GridTable.Data key="items" columnStart={1} columnWidth={isMobile ? 5 : 2}>
                                <ToDoList items={getItems(allFormik?.values?.newPassword)} />
                            </GridTable.Data>,

                            <GridTable.Data key="notContain" columnStart={1} columnWidth={isMobile ? 5 : 2}>
                                <I18n id="onboarding.password.notContainValidations" defaultValue="No debe contener:" />
                            </GridTable.Data>,

                            <GridTable.Data key="notContainItems" columnStart={1} columnWidth={isMobile ? 5 : 2}>
                                <ToDoList items={getNotContainItems(allFormik?.values?.newPassword)} />
                            </GridTable.Data>,
                        ];

                        const repeatedPasswordField = (
                            <GridTable.Data
                                key="repeatedPassword"
                                columnStart={isMobile ? 1 : 4}
                                columnWidth={isMobile ? 5 : 2}>
                                <Field
                                    component={Credential}
                                    idForm={FORM_ID}
                                    name="repeatedPassword"
                                    labelTextDefault={i18nUtils.get(
                                        `${FORM_ID}.repeatedPassword.label`,
                                        "Repetir contraseña",
                                    )}
                                    placeholder={i18nUtils.get(
                                        `${FORM_ID}.repeatedPassword.placeHolder`,
                                        "ej: @Tuempresa22",
                                    )}
                                />
                            </GridTable.Data>
                        );

                        return (
                            <Form className="above-the-fold">
                                <GridTable className={!isMobile && "box box-shadow"}>
                                    <GridTable.Container className="gap-6" columns="5" columnsTemplate="fixed">
                                        <GridTable.Data columnStart={1} columnWidth={isMobile ? 5 : 2}>
                                            <Field
                                                component={Credential}
                                                idForm={FORM_ID}
                                                name="password"
                                                maxLength={maxLength}
                                                labelTextDefault={i18nUtils.get(
                                                    `${FORM_ID}.password.label`,
                                                    "Contraseña anterior",
                                                )}
                                                placeholder={i18nUtils.get(
                                                    `${FORM_ID}.password.placeholder`,
                                                    "ej: @Tuempresa22",
                                                )}
                                                autoFocus={!isMobile}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={1} columnWidth={isMobile ? 5 : 2}>
                                            <Field
                                                component={Credential}
                                                idForm={FORM_ID}
                                                name="newPassword"
                                                maxLength={maxLength}
                                                showStrength
                                                strength={passwordStrength(allFormik?.values?.newPassword)}
                                                labelTextDefault={i18nUtils.get(
                                                    `${FORM_ID}.newPassword.label`,
                                                    "Contraseña nueva",
                                                )}
                                                placeholder={i18nUtils.get(
                                                    `${FORM_ID}.newPassword.label`,
                                                    "ej: @Tuempresa22",
                                                )}
                                                autoFocus={!isMobile}
                                            />
                                        </GridTable.Data>
                                        {isMobile
                                            ? [...validationFields, repeatedPasswordField] // primero validaciones, luego field
                                            : [repeatedPasswordField, ...validationFields] // primero field, luego validaciones
                                        }{" "}
                                    </GridTable.Container>
                                </GridTable>

                                <div className={`grid-table form-actions ${isMobile ? "mt-8" : "mt-2"}`}>
                                    <div className="grid-table-row sm:flex md:grid-cols-12">
                                        {/* // White card */}
                                        <div className="grid-table-data md:col-start-5 md:col-span-4">
                                            <Button
                                                bsStyle="primary"
                                                label="global.continue"
                                                loading={false}
                                                type="submit"
                                                block
                                                disabled={submitIsDisabled(allFormik?.values)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </MainContainer>
        </>
    );
};

ResetExpiredPassword.propTypes = {
    isMobile: PropTypes.bool.isRequired,
};

export default connect()(ResetExpiredPassword);
